import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Checkbox, Typography, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CustomListbox from '../../../components/CustomListbox';

const RoleSelect = ({ roles = [], selected = [], isLoading = false, onChange, placeholder = 'Select roles' }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const selectedObjects = useMemo(() => {
    return roles.filter((role) => selected.includes(role.id));
  }, [roles, selected]);

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={roles}
        loading={isLoading}
        disabled={isLoading}
        value={selectedObjects}
        inputValue={inputValue}
        disableCloseOnSelect
        ListboxComponent={CustomListbox}
        ListboxProps={{
          selectAllLabel: `Select All ${roles.length} roles`,
          onSelectAll: () => onChange(roles.map((role) => role.id)),
          onClearAll: () => onChange([]),
          sx: {
            backgroundColor: '#1e1e1e',
            paddingTop: 0,
            maxHeight: 256,
            overflowY: 'auto',
            '& .MuiAutocomplete-option': {
              color: '#fff',
              padding: '10px 14px',
              '&[aria-selected="true"]': {
                backgroundColor: '#1e1e1e !important',
              },
              '&:hover': {
                backgroundColor: '#4f46e5 !important',
              },
              '&[aria-selected="true"]:hover': {
                backgroundColor: '#4f46e5 !important',
              },
            },
          },
        }}
        onInputChange={(event, value, reason) => {
          if (reason === 'input') {
            setInputValue(value);
          }
        }}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, newValue) => {
          onChange(newValue.map((role) => role.id));
        }}
        renderTags={(value) => {
          if (!value.length || open) return null;
          return <span style={{ marginLeft: 8, color: '#d1d5db' }}>{value.length} Role(s) selected</span>;
        }}
        renderOption={(props, option, { selected: isSelected }) => (
          <li {...props}>
            <Checkbox
              checked={isSelected}
              icon={<span style={{ width: 16, height: 16 }} />}
              checkedIcon={<CheckIcon sx={{ color: '#fff', fontSize: 18 }} />}
              sx={{
                padding: 0,
                marginRight: 1.5,
                marginTop: '3px',
                color: '#fff',
              }}
            />
            <Box>
              <Typography fontSize={14} fontWeight={600} color="#fff">
                {option.name}
              </Typography>
            </Box>
          </li>
        )}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            borderRadius: 2,
            marginTop: 0.5,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedObjects.length === 0 || open ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              sx: {
                paddingLeft: '8px',
                color: '#d1d5db',
                '& input::placeholder': {
                  color: '#9ca3af',
                  opacity: 1,
                },
              },
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default RoleSelect;
